import type { Theme } from '@mui/material';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { type ComponentProps, useCallback, useMemo } from 'react';
import { Label } from '@/pages/ValuationsV2/components/Label';
import { combineSx } from '@/utils/combineSx';
import { CARD_BORDER_RADIUS } from '../../../theme/cardStyles';

const checklistFilterStyles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
    minHeight: '100px',
  },
  emptyContainer: (theme: Theme) => ({
    padding: '1.5rem',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  }),
  list: {
    marginTop: '0.5rem',
    display: 'grid',
    gap: '0.125rem',
  },
  selectAllLabel: (theme: Theme) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
  }),
  selectAllControl: (theme: Theme) => ({
    borderRadius: CARD_BORDER_RADIUS,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c100o15,
    },
    '& .MuiCheckbox-root': {
      padding: '0.125rem 0.375rem',
      color: theme.palette.baseColors.deepSeaNavy.c50,
      '&.Mui-checked': {
        color: theme.palette.baseColors.deepSeaNavy.c100,
      },
    },
    '& .MuiFormControlLabel-label': {
      userSelect: 'none',
    },
  }),
  optionControl: (theme: Theme) => ({
    padding: '0.125rem 0.375rem',
    borderRadius: CARD_BORDER_RADIUS,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c100o15,
    },
    '& .MuiCheckbox-root': {
      padding: '0.125rem 0.375rem',
      color: theme.palette.baseColors.deepSeaNavy.c50,
      '&.Mui-checked': {
        color: theme.palette.baseColors.deepSeaNavy.c100,
      },
    },
    '& .MuiFormControlLabel-label': {
      color: theme.palette.text.primary,
      userSelect: 'none',
    },
  }),
};

type ChecklistOption = {
  id: string;
  name: string;
  isDisabled?: boolean;
};

type ChecklistFilterProps<T extends ChecklistOption> = {
  value: T[];
  options: T[];
  onChange: (selected: T[]) => void;
  label: string;
  loading?: boolean;
  dataId?: string;
  selectAllLabel?: string;
  emptyMessage?: string;
} & Omit<ComponentProps<typeof Box>, 'onChange'>;

const ChecklistFilter = <T extends ChecklistOption>({
  value,
  options,
  onChange,
  label,
  loading = false,
  dataId,
  selectAllLabel = 'Select All',
  emptyMessage = 'No options available',
  sx,
}: ChecklistFilterProps<T>) => {
  const disableRipple = process.env.NODE_ENV === 'test';

  const selectedIds = useMemo(() => new Set(value.map(item => item.id)), [value]);

  const enabledOptions = useMemo(() => options.filter(option => !option.isDisabled), [options]);

  const isAllSelected = useMemo(
    () => enabledOptions.length > 0 && enabledOptions.every(option => selectedIds.has(option.id)),
    [enabledOptions, selectedIds],
  );

  const isIndeterminate = useMemo(
    () =>
      !isAllSelected &&
      enabledOptions.length > 0 &&
      enabledOptions.some(option => selectedIds.has(option.id)),
    [enabledOptions, selectedIds, isAllSelected],
  );

  const handleToggleAll = useCallback((): void => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...enabledOptions]);
    }
  }, [isAllSelected, enabledOptions, onChange]);

  const handleToggleOption = useCallback(
    (option: T): void => {
      const isSelected = selectedIds.has(option.id);
      if (isSelected) {
        onChange(value.filter(item => item.id !== option.id));
      } else {
        onChange([...value, option]);
      }
    },
    [selectedIds, value, onChange],
  );

  if (loading) {
    return (
      <Box
        sx={checklistFilterStyles.loadingContainer}
        data-id={`${dataId ?? 'checklist-filter'}-loading`}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (enabledOptions.length === 0) {
    return (
      <Box
        sx={checklistFilterStyles.emptyContainer}
        data-id={`${dataId ?? 'checklist-filter'}-empty`}
      >
        <Typography variant='body2' color='text.secondary'>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={combineSx(sx)} data-id={dataId ?? 'checklist-filter'}>
      <Label label={label} />
      <FormGroup sx={checklistFilterStyles.list}>
        {enabledOptions.length > 1 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleToggleAll}
                disableRipple={disableRipple}
                data-id={`${dataId ?? 'checklist'}-select-all`}
              />
            }
            label={
              <Typography variant='caption' sx={checklistFilterStyles.selectAllLabel}>
                {selectAllLabel}
              </Typography>
            }
            sx={checklistFilterStyles.selectAllControl}
          />
        )}
        {enabledOptions.map(option => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                checked={selectedIds.has(option.id)}
                onChange={() => handleToggleOption(option)}
                disableRipple={disableRipple}
                data-id={`${dataId ?? 'checklist'}-option-${option.id}`}
              />
            }
            label={option.name}
            sx={enabledOptions.length > 1 ? checklistFilterStyles.optionControl : {}}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export { ChecklistFilter, type ChecklistFilterProps };
